import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Toggle } from '@/ui';
import StatusToggleFrame from '../StatusToggleFrame/StatusToggleFrame';

/**
 * Typed here so TypeScript callers see the optional props as optional. Without it TS
 * infers every destructured name as required and each .tsx consumer has to pass all eight.
 *
 * @param {{
 *   isActive?: boolean | null,
 *   modalType?: string,
 *   errors?: { isUrlMissing?: boolean, isUserAttributeMissing?: boolean } | null,
 *   getErrors?: () => { isUrlMissing?: boolean, isUserAttributeMissing?: boolean } | null | undefined,
 *   disabled?: boolean,
 *   className?: string,
 *   framed?: boolean,
 *   onClickSubmit: (active: boolean) => void,
 *   content?: { name?: string } | null,
 * }} props
 */
const ActiveStatusModal = ({
  isActive: initialActive,
  modalType,
  errors: propErrors,
  getErrors,
  disabled,
  className,
  framed = false,
  onClickSubmit,
  content,
}) => {
  const [isActive, setIsActive] = useState(initialActive);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setIsActive(initialActive);
  }, [initialActive]);

  const toggleSuccess = () => {
    setSuccess((prev) => !prev);
    setIsActive((prev) => !prev);
  };

  const saveSuccess = () => {
    onClickSubmit(isActive);
    setSuccess((prev) => !prev);
  };

  const statusErrors = propErrors || getErrors?.();
  const isUrlMissing = statusErrors ? statusErrors.isUrlMissing : false;
  const isUserAttributeMissing = statusErrors ? statusErrors.isUserAttributeMissing : false;
  const isSwitchDisabled =
    initialActive === null || disabled === true || isUrlMissing === true || isUserAttributeMissing === true;

  const toggle = (
    <Toggle
      className='react-switch'
      disabled={isSwitchDisabled}
      onChange={toggleSuccess}
      checked={isActive != null ? isActive : false}
      offColor='error'
      label={isActive ? 'Active' : 'Inactive'}
    />
  );

  return (
    <>
      {/* `framed` is opt-in for the detail headers; the list tables keep the bare switch. The
          frame follows the local state so it previews the pending colour while the confirm
          dialog is open, and snaps back with the switch if the user cancels. */}
      {framed ? <StatusToggleFrame isActive={isActive}>{toggle}</StatusToggleFrame> : toggle}
      <div>
        <Modal isOpen={success} toggle={toggleSuccess} tone='danger' className={className}>
          <ModalHeader toggle={toggleSuccess}>Active Status</ModalHeader>
          <ModalBody>
            <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
              <WarningAmber sx={{ fontSize: '3rem', color: 'error.main', flex: '0 0 auto' }} />
              <div>
                <Typography variant='h6'>{content && content.name}</Typography>
                <Typography>
                  Are you sure you want to{!isActive ? ' deactivate ' : ' activate '}this {modalType}?
                </Typography>
              </div>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button color='danger' onClick={saveSuccess}>
              {!isActive ? 'Deactivate ' : 'Activate '}
            </Button>{' '}
            <Button color='secondary' onClick={toggleSuccess}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};
export default ActiveStatusModal;
